import { useRef, useEffect, useState, useCallback, forwardRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import api from "@/lib/api";
import { toast } from "sonner";
import GameHUD from "@/components/game/GameHUD";
import { useARBackground, ARVideoLayer } from "@/components/game/ARBackground";
import { Button } from "@/components/ui/button";
import { X, VideoCamera } from "@phosphor-icons/react";

/**
 * Game3D — Top-level component that wraps the Three.js Canvas + HUD.
 * Implements a simplified padel physics: ball gravity, court bounces, side-wall bounces,
 * paddle hits triggered by the HUD action buttons.
 *
 * Court dimensions: 20m long × 10m wide (split in half by net at z=0).
 * Camera: behind player, slight high angle.
 */
export default function Game3D({ matchState, characters = [], onExit }) {
  const { player_character, partner_character, opponents = [], mode, match_id } = matchState || {};

  // Game state refs (avoid re-render every frame)
  const playerPos = useRef(new THREE.Vector3(0, 0, 6));      // player team side z > 0
  const partnerPos = useRef(new THREE.Vector3(-3, 0, 6));
  const opp1Pos = useRef(new THREE.Vector3(0, 0, -6));
  const opp2Pos = useRef(new THREE.Vector3(-3, 0, -6));
  const ballPos = useRef(new THREE.Vector3(0, 1.5, 0));
  const ballVel = useRef(new THREE.Vector3(0, 0, 0));            // starts at rest
  const ballSpeed = useRef(0);                                   // for HUD display
  const joystickRef = useRef({ x: 0, y: 0 });                    // -1..1
  const lastHitBy = useRef("none");                              // who hit last
  const lastShotType = useRef("drive");
  const shotDirectionRef = useRef("center");                     // for next shot
  const playerScore = useRef(0);
  const opponentScore = useRef(0);
  const gameWon = useRef(0);                                      // player games
  const gameLostStatus = useRef(0);                               // opp games
  const cooldown = useRef(0);                                     // ms between shots
  const servingRef = useRef("player");                            // "player" | "opponent" — who serves next
  const stateRef = useRef("waiting_serve");                       // "waiting_serve" | "playing"
  const botServeTimer = useRef(0);                                // ms countdown for bot auto-serve

  // Fatigue (1.0 = fresh, 0 = exhausted). Decreases with shots, regenerates slowly.
  const fatiguePlayerRef = useRef(1.0);
  const fatigueOpponentRef = useRef(1.0);

  // Timing window for next shot (set when ball is incoming)
  const timingWindowRef = useRef({ active: false, optimal_t: 0, opened_at: 0 });

  // UI state
  const [scoreUI, setScoreUI] = useState({ p: 0, o: 0, gp: 0, go: 0 });
  const [speedUI, setSpeedUI] = useState(0);
  const [matchOver, setMatchOver] = useState(null); // null | "player" | "opponent"
  const [stateUI, setStateUI] = useState("waiting_serve"); // mirrors stateRef for HUD
  const [serverUI, setServerUI] = useState("player"); // mirrors servingRef
  const [fatigueUI, setFatigueUI] = useState({ player: 1.0, opponent: 1.0 });
  const [timingUI, setTimingUI] = useState({ show: false, progress: 0, zoneStart: 0.4, zoneEnd: 0.6 });

  // AR mode
  const ar = useARBackground();
  const arActive = ar.active;

  // Initialize ball position for serve
  const setupServe = useCallback(() => {
    stateRef.current = "waiting_serve";
    setStateUI("waiting_serve");
    setServerUI(servingRef.current);
    if (servingRef.current === "player") {
      ballPos.current.set(playerPos.current.x, 1.4, playerPos.current.z - 0.4);
    } else {
      ballPos.current.set(opp1Pos.current.x, 1.4, opp1Pos.current.z + 0.4);
      botServeTimer.current = 800; // bot will auto-serve after ~0.8s
    }
    ballVel.current.set(0, 0, 0);
    ballSpeed.current = 0;
    lastHitBy.current = "none";
  }, []);

  // Mount: first serve setup
  useEffect(() => {
    setupServe();
  }, [setupServe]);

  // Serve action — used by both player (via PUSH button) and bot (auto)
  const doServe = useCallback((who) => {
    if (stateRef.current !== "waiting_serve") return;
    if (who === "player") {
      const power = 12;
      const targetX = (Math.random() - 0.5) * 4;
      const targetZ = -6;
      const fromX = playerPos.current.x;
      const fromZ = playerPos.current.z;
      const dx = targetX - fromX, dz = targetZ - fromZ;
      const len = Math.sqrt(dx * dx + dz * dz) || 1;
      ballPos.current.set(fromX, 1.6, fromZ - 0.5);
      ballVel.current.set((dx / len) * power, 3.5, (dz / len) * power);
      lastHitBy.current = "player";
      ballSpeed.current = Math.round(power * 7.2);
    } else {
      // Bot serve: SLOWER + AIMED at center of player side so ball bounces and player can hit
      const power = 8.5; // softer serve so it bounces in front of player
      const fromX = opp1Pos.current.x;
      const fromZ = opp1Pos.current.z;
      // Aim slightly ahead of player but keep a predictable landing zone
      const targetX = THREE.MathUtils.clamp(playerPos.current.x + (Math.random() - 0.5) * 1.5, -2.5, 2.5);
      const targetZ = 4.5; // bounce in front of player (player at z ~6)
      const dx = targetX - fromX, dz = targetZ - fromZ;
      const len = Math.sqrt(dx * dx + dz * dz) || 1;
      ballPos.current.set(fromX, 1.6, fromZ + 0.5);
      // Higher arc so ball bounces nicely on player side
      ballVel.current.set((dx / len) * power, 5.5, (dz / len) * power);
      lastHitBy.current = "opponent";
      ballSpeed.current = Math.round(power * 7.2);
    }
    stateRef.current = "playing";
    setStateUI("playing");
    botServeTimer.current = 0;
  }, []);

  // Reset rally and prep next serve (alternate server)
  const onPointScored = useCallback((scoredBy) => {
    if (scoredBy === "player") playerScore.current++;
    else opponentScore.current++;
    // Loser of point serves next
    servingRef.current = scoredBy === "player" ? "opponent" : "player";
    setupServe();
  }, [setupServe]);

  // Finalize match
  const finishMatch = useCallback(async (winner) => {
    if (matchOver) return;
    setMatchOver(winner);
    try {
      await api.post(`/game/match/${match_id}/finish`, {
        winner_team: winner,
        score: `${gameWon.current}-${gameLostStatus.current}`,
        duration_seconds: 0,
      });
      if (winner === "player") toast.success("¡Ganaste! +5 DYD");
      else toast.error("Perdiste. Intenta de nuevo.");
    } catch (e) { /* non-blocking */ }
  }, [match_id, matchOver]);

  // ─── HUD handlers ───
  const onJoystick = useCallback((v) => { joystickRef.current = v; }, []);
  const onShot = useCallback((shotType) => {
    // If we're waiting to serve and it's our turn, any shot button serves
    if (stateRef.current === "waiting_serve" && servingRef.current === "player") {
      doServe("player");
      return;
    }
    if (stateRef.current !== "playing") return null;
    lastShotType.current = shotType;
    // If ball is close to player, hit it
    const dx = ballPos.current.x - playerPos.current.x;
    const dz = ballPos.current.z - playerPos.current.z;
    const dy = ballPos.current.y;
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist < 4.5 && dy < 4.0 && cooldown.current <= 0) {
      // ─── TIMING evaluation ───
      let timingResult = "good";
      let timingMult = 1.0;
      if (timingWindowRef.current.active) {
        const now = performance.now();
        const elapsed = now - timingWindowRef.current.opened_at;
        const total = timingWindowRef.current.window_ms || 800;
        const t = Math.max(0, Math.min(1, elapsed / total));
        const optimal = 0.5; // center of window
        const deltaFromOptimal = Math.abs(t - optimal);
        if (deltaFromOptimal < 0.1) { timingResult = "perfect"; timingMult = 1.25; }
        else if (deltaFromOptimal < 0.2) { timingResult = "good"; timingMult = 1.0; }
        else { timingResult = "miss"; timingMult = 0.6; }
        timingWindowRef.current.active = false;
      }
      // ─── STATS-driven power & control ───
      const stats = player_character?.stats || { power: 80, speed: 80, control: 80 };
      const powerStatMult = 0.7 + (stats.power / 100) * 0.6; // 0.7 .. 1.3
      const controlStatMult = 0.7 + (stats.control / 100) * 0.6; // affects accuracy
      const fatigueMult = 0.6 + fatiguePlayerRef.current * 0.4; // tired = weaker
      const basePower = {
        drive: 14, bandeja: 11, bolea: 12, rulo: 9, x3: 20,
      }[shotType] || 12;
      const power = basePower * powerStatMult * fatigueMult * timingMult;

      const dir = shotDirectionRef.current;
      let targetX = 0;
      let targetZ = -6;
      if (dir === "left") targetX = -3;
      else if (dir === "right") targetX = 3;
      else if (dir === "up") targetZ = -8;
      else if (dir === "down") targetZ = -3;
      else if (dir === "push") { targetZ = -2; }

      // Inaccuracy when control low or timing missed
      const inaccuracy = (1 - controlStatMult) * 2 + (timingResult === "miss" ? 2.5 : timingResult === "good" ? 0.8 : 0);
      targetX += (Math.random() - 0.5) * inaccuracy;

      const fromX = playerPos.current.x;
      const fromZ = playerPos.current.z;
      const dirX = targetX - fromX;
      const dirZ = targetZ - fromZ;
      const len = Math.sqrt(dirX * dirX + dirZ * dirZ) || 1;
      ballVel.current.set((dirX / len) * power, 4 + (shotType === "rulo" ? 3 : 0), (dirZ / len) * power);
      ballSpeed.current = Math.round(power * 7.2);
      lastHitBy.current = "player";
      cooldown.current = 250;
      ballPos.current.set(playerPos.current.x, 1.2, playerPos.current.z - 0.5);
      // Fatigue cost
      fatiguePlayerRef.current = Math.max(0, fatiguePlayerRef.current - 0.018);
      return timingResult;
    }
    return null;
  }, [doServe]);
  const onDirection = useCallback((d) => {
    shotDirectionRef.current = d;
    // PUSH is special: when waiting and it's player's turn, PUSH triggers serve
    if (d === "push" && stateRef.current === "waiting_serve" && servingRef.current === "player") {
      doServe("player");
    }
  }, [doServe]);

  return (
    <div className="fixed inset-0 bg-black z-50" data-testid="game3d-root">
      {/* AR camera background (visible only when AR enabled) */}
      <ARVideoLayer videoRef={ar.videoRef} visible={arActive} />
      <Canvas
        camera={{ position: [0, 6, 16], fov: 50 }}
        dpr={[1, 1.5]}
        gl={{
          antialias: false,
          powerPreference: "default",
          failIfMajorPerformanceCaveat: false,
          preserveDrawingBuffer: false,
          alpha: arActive,
        }}
        style={{ background: arActive ? "transparent" : undefined, position: "relative", zIndex: 1 }}
        onCreated={({ gl }) => {
          if (arActive) {
            gl.setClearColor(0x000000, 0);
          }
          const canvas = gl.domElement;
          canvas.addEventListener("webglcontextlost", (e) => {
            e.preventDefault();
            console.warn("[Game3D] WebGL context lost");
          }, false);
        }}
      >
        <SceneContent
          playerPos={playerPos}
          partnerPos={partnerPos}
          opp1Pos={opp1Pos}
          opp2Pos={opp2Pos}
          ballPos={ballPos}
          ballVel={ballVel}
          ballSpeed={ballSpeed}
          joystickRef={joystickRef}
          lastHitBy={lastHitBy}
          lastShotType={lastShotType}
          cooldown={cooldown}
          playerScore={playerScore}
          opponentScore={opponentScore}
          gameWon={gameWon}
          gameLostStatus={gameLostStatus}
          mode={mode}
          setScoreUI={setScoreUI}
          setSpeedUI={setSpeedUI}
          onMatchEnd={finishMatch}
          playerChar={player_character}
          partnerChar={partner_character}
          opponents={opponents}
          stateRef={stateRef}
          servingRef={servingRef}
          botServeTimer={botServeTimer}
          doServe={doServe}
          onPointScored={onPointScored}
          arActive={arActive}
          fatiguePlayerRef={fatiguePlayerRef}
          fatigueOpponentRef={fatigueOpponentRef}
          timingWindowRef={timingWindowRef}
          setFatigueUI={setFatigueUI}
          setTimingUI={setTimingUI}
        />
      </Canvas>

      {/* HUD overlay */}
      <GameHUD
        player={player_character}
        opponent={opponents[0]}
        scoreUI={scoreUI}
        speed={speedUI}
        onJoystick={onJoystick}
        onShot={onShot}
        onDirection={onDirection}
        gameState={stateUI}
        server={serverUI}
        fatiguePlayer={fatigueUI.player}
        fatigueOpponent={fatigueUI.opponent}
        timingZone={timingUI}
      />

      {/* Top controls: AR toggle + Exit */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        <Button
          onClick={() => arActive ? ar.stop() : ar.requestPermission()}
          className={`border h-8 px-3 ${arActive ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-black/60 border-white/20 text-white hover:bg-purple-500/80"}`}
          data-testid="game-ar-toggle"
        >
          <VideoCamera size={14} className="mr-1" weight={arActive ? "fill" : "regular"} /> {arActive ? "AR ON" : "AR"}
        </Button>
        <Button
          onClick={onExit}
          className="bg-black/60 border border-white/20 text-white hover:bg-red-500/80 h-8 px-3"
          data-testid="game-exit-btn"
        >
          <X size={14} className="mr-1" /> SALIR
        </Button>
      </div>

      {/* Match over overlay */}
      {matchOver && (
        <div className="absolute inset-0 bg-black/85 flex items-center justify-center z-30" data-testid="match-over-overlay">
          <div className="bg-[#141414] border-2 border-[#CCFF00]/40 rounded-lg p-8 text-center max-w-md">
            <div className="text-6xl mb-4">{matchOver === "player" ? "🏆" : "💔"}</div>
            <h2 className="font-['Barlow_Condensed'] text-4xl font-black uppercase text-white mb-2">
              {matchOver === "player" ? "¡Ganaste!" : "Perdiste"}
            </h2>
            <p className="text-sm text-gray-400 mb-6">{matchOver === "player" ? "+5 DYD Moneda" : "Mejor suerte la próxima."}</p>
            <Button onClick={onExit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase">
              Volver al Lobby
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── 3D Scene ───
function SceneContent({
  playerPos, partnerPos, opp1Pos, opp2Pos, ballPos, ballVel, ballSpeed,
  joystickRef, lastHitBy, lastShotType, cooldown,
  playerScore, opponentScore, gameWon, gameLostStatus,
  mode, setScoreUI, setSpeedUI, onMatchEnd,
  playerChar, partnerChar, opponents,
  stateRef, servingRef, botServeTimer, doServe, onPointScored,
  arActive = false,
  fatiguePlayerRef, fatigueOpponentRef, timingWindowRef,
  setFatigueUI, setTimingUI,
}) {
  const ballRef = useRef();
  const playerRef = useRef();
  const partnerRef = useRef();
  const opp1Ref = useRef();
  const opp2Ref = useRef();
  const lastSyncT = useRef(0);

  useFrame((state, delta) => {
    const dt = Math.min(delta, 0.05);
    // Cooldown timer
    if (cooldown.current > 0) cooldown.current -= dt * 1000;

    // Player movement from joystick (always allowed, even while waiting)
    const j = joystickRef.current;
    const speed = 6;
    playerPos.current.x += j.x * speed * dt;
    playerPos.current.z += j.y * speed * dt;
    playerPos.current.x = THREE.MathUtils.clamp(playerPos.current.x, -4.5, 4.5);
    playerPos.current.z = THREE.MathUtils.clamp(playerPos.current.z, 0.8, 9.5);

    // ─── WAITING SERVE STATE ───
    if (stateRef.current === "waiting_serve") {
      // Bot auto-serves after delay
      if (servingRef.current === "opponent") {
        botServeTimer.current -= dt * 1000;
        // Keep ball glued to bot serving position
        ballPos.current.x = opp1Pos.current.x;
        ballPos.current.z = opp1Pos.current.z + 0.4;
        ballPos.current.y = 1.4 + Math.sin(state.clock.elapsedTime * 6) * 0.05;
        if (botServeTimer.current <= 0) {
          doServe("opponent");
        }
      } else {
        // Player serving — keep ball with player
        ballPos.current.x = playerPos.current.x;
        ballPos.current.z = playerPos.current.z - 0.4;
        ballPos.current.y = 1.4 + Math.sin(state.clock.elapsedTime * 6) * 0.05;
      }
      ballVel.current.set(0, 0, 0);
      ballSpeed.current = 0;
      // Apply visuals and sync UI, then skip rest of physics
      if (ballRef.current) ballRef.current.position.copy(ballPos.current);
      if (playerRef.current) playerRef.current.position.copy(playerPos.current).add(new THREE.Vector3(0, 0.9, 0));
      if (opp1Ref.current) opp1Ref.current.position.copy(opp1Pos.current).add(new THREE.Vector3(0, 0.9, 0));
      if (mode === "doubles") {
        if (partnerRef.current) partnerRef.current.position.copy(partnerPos.current).add(new THREE.Vector3(0, 0.9, 0));
        if (opp2Ref.current) opp2Ref.current.position.copy(opp2Pos.current).add(new THREE.Vector3(0, 0.9, 0));
      }
      // Camera follow
      state.camera.position.set(playerPos.current.x * 0.3, 8, playerPos.current.z + 8);
      state.camera.lookAt(0, 1, -2);
      lastSyncT.current += dt;
      if (lastSyncT.current > 0.15) {
        lastSyncT.current = 0;
        setScoreUI({ p: playerScore.current, o: opponentScore.current, gp: gameWon.current, go: gameLostStatus.current });
        setSpeedUI(0);
      }
      return;
    }

    // ─── BOT AI MOVEMENT (stats-driven speed + fatigue) ───
    const oppChar0 = opponents[0] || {};
    const oppStats0 = oppChar0.stats || { power: 80, speed: 80, control: 80 };
    const botSpeedStat = 0.65 + (oppStats0.speed / 100) * 0.55;
    const moveBot = (botPos, isPartner) => {
      const ballOnTheirSide = ballPos.current.z < 0 && ballVel.current.z < 0;
      let targetX = botPos.current.x;
      let targetZ = isPartner ? -7 : -5;
      if (ballOnTheirSide || lastHitBy.current === "player") {
        const timeToReach = Math.max(0.1, Math.abs(ballPos.current.z - targetZ) / (Math.abs(ballVel.current.z) + 0.1));
        const predX = ballPos.current.x + ballVel.current.x * Math.min(timeToReach, 0.8);
        targetX = isPartner ? predX - 1.5 : predX;
        if (Math.abs(ballPos.current.z - botPos.current.z) < 3) {
          targetZ = ballPos.current.z + 0.5;
        }
      }
      const dx = targetX - botPos.current.x;
      const dz = targetZ - botPos.current.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      const botSpeed = 7.0 * botSpeedStat * (0.6 + fatigueOpponentRef.current * 0.4);
      if (dist > 0.1) {
        botPos.current.x += (dx / dist) * botSpeed * dt;
        botPos.current.z += (dz / dist) * botSpeed * dt;
      }
      botPos.current.x = THREE.MathUtils.clamp(botPos.current.x, -4.5, 4.5);
      botPos.current.z = THREE.MathUtils.clamp(botPos.current.z, -9.5, -0.8);
    };
    moveBot(opp1Pos, false);
    if (mode === "doubles") moveBot(opp2Pos, true);

    // Partner AI (defensive)
    if (mode === "doubles") {
      const targetX = ballPos.current.x + 2;
      const dx = targetX - partnerPos.current.x;
      const dz = 7 - partnerPos.current.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist > 0.1) {
        partnerPos.current.x += (dx / dist) * 3 * dt;
        partnerPos.current.z += (dz / dist) * 3 * dt;
      }
      partnerPos.current.x = THREE.MathUtils.clamp(partnerPos.current.x, -4.5, 4.5);
      partnerPos.current.z = THREE.MathUtils.clamp(partnerPos.current.z, 0.8, 9.5);
    }

    // Ball physics
    ballVel.current.y -= 9.81 * dt;
    ballPos.current.addScaledVector(ballVel.current, dt);

    // Ground bounce
    if (ballPos.current.y < 0.15) {
      ballPos.current.y = 0.15;
      ballVel.current.y *= -0.65;
      ballVel.current.x *= 0.92;
      ballVel.current.z *= 0.92;
      if (Math.abs(ballPos.current.x) < 5) {
        // Award point if ball bounces twice (low speed)
        if (ballVel.current.length() < 1.5) {
          if (ballPos.current.z > 0) {
            // bounced on player side → opponent scored
            onPointScored("opponent");
          } else {
            onPointScored("player");
          }
          return;
        }
      }
    }

    // Out of bounds → point to opponent of last hitter
    if (Math.abs(ballPos.current.x) > 6 || Math.abs(ballPos.current.z) > 12) {
      if (lastHitBy.current === "player") onPointScored("opponent");
      else onPointScored("player");
      return;
    }

    // Wall bounce — DIFFERENTIATED PHYSICS:
    // Back walls (z = ±10) = GLASS: clean, predictable rebound (factor -0.78, no jitter)
    // Side walls (x = ±5) = MALLA (mesh): irregular, dampened rebound (factor -0.45, random jitter)
    if (ballPos.current.z > 10) {
      ballPos.current.z = 10;
      ballVel.current.z *= -0.78; // clean glass
      // No jitter — predictable
    }
    if (ballPos.current.z < -10) {
      ballPos.current.z = -10;
      ballVel.current.z *= -0.78;
    }
    if (ballPos.current.x > 5) {
      ballPos.current.x = 5;
      ballVel.current.x *= -0.45; // damped mesh
      // Irregular: add random perturbation
      ballVel.current.z += (Math.random() - 0.5) * 3;
      ballVel.current.y += Math.random() * 0.5;
    }
    if (ballPos.current.x < -5) {
      ballPos.current.x = -5;
      ballVel.current.x *= -0.45;
      ballVel.current.z += (Math.random() - 0.5) * 3;
      ballVel.current.y += Math.random() * 0.5;
    }

    // ─── TIMING WINDOW: open when ball is approaching player's reach ───
    if (lastHitBy.current === "opponent" && stateRef.current === "playing") {
      const dxp = ballPos.current.x - playerPos.current.x;
      const dzp = ballPos.current.z - playerPos.current.z;
      const distP = Math.sqrt(dxp * dxp + dzp * dzp);
      const ballApproaching = ballVel.current.z > 0; // moving toward player side
      const inRange = distP < 6 && ballApproaching && ballPos.current.z > -2;
      if (inRange && !timingWindowRef.current.active) {
        timingWindowRef.current.active = true;
        timingWindowRef.current.opened_at = performance.now();
        timingWindowRef.current.window_ms = 800;
      }
    }

    // Bot hit detection (stats-driven power + fatigue cost)
    const tryBotHit = (botPos, isPartner) => {
      const dx = ballPos.current.x - botPos.current.x;
      const dz = ballPos.current.z - botPos.current.z;
      const dy = ballPos.current.y;
      const dist = Math.sqrt(dx * dx + dz * dz);
      const ballOnBotSide = ballPos.current.z < 0.5;
      const inRange = dist < 3.8 && dy < 4.0;
      if (inRange && lastHitBy.current === "player" && ballOnBotSide) {
        const oppStats = (opponents[isPartner ? 1 : 0] || {}).stats || { power: 80, control: 80 };
        const powerMult = 0.7 + (oppStats.power / 100) * 0.6; // 0.7..1.3
        const controlMult = 0.7 + (oppStats.control / 100) * 0.6;
        const fatigueMult = 0.6 + fatigueOpponentRef.current * 0.4;
        const noise = (1 - controlMult) * 2;
        const targetX = THREE.MathUtils.clamp(playerPos.current.x + (Math.random() - 0.5) * 2.5 + (Math.random() - 0.5) * noise, -4, 4);
        const targetZ = 5.5 + (Math.random() - 0.5) * 2.5;
        const dirX = targetX - botPos.current.x;
        const dirZ = targetZ - botPos.current.z;
        const len = Math.sqrt(dirX * dirX + dirZ * dirZ) || 1;
        const basePower = 12 + Math.random() * 2;
        const power = basePower * powerMult * fatigueMult;
        ballVel.current.set((dirX / len) * power, 4.0 + Math.random() * 0.8, (dirZ / len) * power);
        ballSpeed.current = Math.round(power * 7.2);
        lastHitBy.current = "opponent";
        fatigueOpponentRef.current = Math.max(0, fatigueOpponentRef.current - 0.014);
      }
    };
    tryBotHit(opp1Pos, false);
    if (mode === "doubles") tryBotHit(opp2Pos, true);

    // Apply to meshes
    if (ballRef.current) ballRef.current.position.copy(ballPos.current);
    if (playerRef.current) playerRef.current.position.copy(playerPos.current).add(new THREE.Vector3(0, 0.9, 0));
    if (partnerRef.current && mode === "doubles") partnerRef.current.position.copy(partnerPos.current).add(new THREE.Vector3(0, 0.9, 0));
    if (opp1Ref.current) opp1Ref.current.position.copy(opp1Pos.current).add(new THREE.Vector3(0, 0.9, 0));
    if (opp2Ref.current && mode === "doubles") opp2Ref.current.position.copy(opp2Pos.current).add(new THREE.Vector3(0, 0.9, 0));

    // Camera follow
    state.camera.position.set(
      playerPos.current.x * 0.3,
      8,
      playerPos.current.z + 8
    );
    state.camera.lookAt(0, 1, -2);

    // Sync UI every ~150ms
    lastSyncT.current += dt;
    if (lastSyncT.current > 0.15) {
      lastSyncT.current = 0;
      setScoreUI({ p: playerScore.current, o: opponentScore.current, gp: gameWon.current, go: gameLostStatus.current });
      setSpeedUI(Math.round(ballSpeed.current));
    }

    // Win game: first to 4 points (simplified) — point already scored via onPointScored
    if (playerScore.current >= 4 && playerScore.current - opponentScore.current >= 2) {
      gameWon.current++;
      playerScore.current = 0; opponentScore.current = 0;
      if (gameWon.current >= 3) onMatchEnd?.("player");
    } else if (opponentScore.current >= 4 && opponentScore.current - playerScore.current >= 2) {
      gameLostStatus.current++;
      playerScore.current = 0; opponentScore.current = 0;
      if (gameLostStatus.current >= 3) onMatchEnd?.("opponent");
    }
  });

  // Accent colors from characters
  const playerColor = playerChar?.accent_color || "#1E90FF";
  const partnerColor = partnerChar?.accent_color || "#00BFFF";
  const oppColor1 = opponents[0]?.accent_color || "#FF4444";
  const oppColor2 = opponents[1]?.accent_color || "#FF8800";

  return (
    <>
      <ambientLight intensity={0.55} />
      <directionalLight position={[10, 15, 10]} intensity={1.0} />
      <pointLight position={[0, 8, 0]} intensity={0.6} color="#CCFF00" />

      {/* Sky / background — hidden in AR */}
      {!arActive && <color attach="background" args={["#020817"]} />}
      {!arActive && <fog attach="fog" args={["#020817", 18, 40]} />}

      {/* Court floor — hidden in AR (transparent so camera shows through) */}
      {!arActive && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
          <planeGeometry args={[10, 20]} />
          <meshStandardMaterial color="#1565c0" roughness={0.7} />
        </mesh>
      )}

      {/* Court lines (always visible — they help orient in AR too) */}
      <CourtLines />

      {/* Net */}
      {!arActive && (
        <>
          <mesh position={[0, 0.5, 0]}>
            <boxGeometry args={[10, 1, 0.05]} />
            <meshStandardMaterial color="#ffffff" transparent opacity={0.5} />
          </mesh>
          <mesh position={[0, 1.0, 0]}>
            <boxGeometry args={[10.2, 0.05, 0.06]} />
            <meshStandardMaterial color="#ffffff" />
          </mesh>
        </>
      )}

      {/* Glass walls — hidden in AR */}
      {!arActive && (
        <>
          <mesh position={[0, 1.5, 10]}>
            <boxGeometry args={[10, 3, 0.1]} />
            <meshStandardMaterial color="#88ccff" transparent opacity={0.25} />
          </mesh>
          <mesh position={[0, 1.5, -10]}>
            <boxGeometry args={[10, 3, 0.1]} />
            <meshStandardMaterial color="#88ccff" transparent opacity={0.25} />
          </mesh>
          <mesh position={[5, 1.5, 0]}>
            <boxGeometry args={[0.1, 3, 20]} />
            <meshStandardMaterial color="#88ccff" transparent opacity={0.2} />
          </mesh>
          <mesh position={[-5, 1.5, 0]}>
            <boxGeometry args={[0.1, 3, 20]} />
            <meshStandardMaterial color="#88ccff" transparent opacity={0.2} />
          </mesh>
        </>
      )}

      {/* Ball */}
      <mesh ref={ballRef} position={[0, 1.5, 0]}>
        <sphereGeometry args={[0.18, 16, 16]} />
        <meshStandardMaterial color="#e0ff00" emissive="#aaff00" emissiveIntensity={0.6} />
      </mesh>

      {/* Player */}
      <PlayerCapsule ref={playerRef} color={playerColor} />
      {mode === "doubles" && <PlayerCapsule ref={partnerRef} color={partnerColor} />}
      <PlayerCapsule ref={opp1Ref} color={oppColor1} />
      {mode === "doubles" && <PlayerCapsule ref={opp2Ref} color={oppColor2} />}
    </>
  );
}

function CourtLines() {
  return (
    <>
      {/* Service line (3m from net each side) */}
      <mesh position={[0, 0.01, 3]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[10, 0.08]} />
        <meshBasicMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0, 0.01, -3]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[10, 0.08]} />
        <meshBasicMaterial color="#ffffff" />
      </mesh>
      {/* Center line */}
      <mesh position={[0, 0.01, 1.5]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[0.08, 3]} />
        <meshBasicMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0, 0.01, -1.5]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[0.08, 3]} />
        <meshBasicMaterial color="#ffffff" />
      </mesh>
    </>
  );
}

const PlayerCapsule = forwardRef(function PlayerCapsule({ color }, ref) {
  return (
    <group ref={ref}>
      <mesh position={[0, 0.4, 0]}>
        <capsuleGeometry args={[0.35, 1.0, 6, 12]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.25} />
      </mesh>
      {/* Paddle */}
      <mesh position={[0.5, 0.4, 0]} rotation={[0, 0, Math.PI / 6]}>
        <boxGeometry args={[0.35, 0.1, 0.04]} />
        <meshStandardMaterial color="#222222" />
      </mesh>
    </group>
  );
});
